import { MiniMap, MiniMapProps } from "@/components/r3f/utils/minimap/minimap";
import { useAppSelector } from "@/store/store-hooks";
import { SceneFilter } from "@/types/scene-filter";
import { isIElementImg2d, isIElementImg360 } from "@faro-lotv/ielement-types";
import { SupportedCamera } from "@faro-lotv/lotv";
import { JSX, useCallback } from "react";
import { Vector3 } from "three";
import { selectIsPanoAligned } from "../walk-mode/walk-scene-selectors";
import { WalkSceneActiveElement } from "../walk-mode/walk-types";

type SplitMiniMapProps = Pick<
  MiniMapProps,
  "trackingElement" | "canvasElement" | "actions" | "onPlaceholderClicked"
> & {
  /** Main walk element */
  mainWalkElement: WalkSceneActiveElement;
  /** Compare walk element */
  compareWalkElement: WalkSceneActiveElement;
  /** Main scene filter */
  mainSceneFilter: SceneFilter;
  /** Compare scene filter */
  compareSceneFilter: SceneFilter;
  /** Camera used in the left view */
  leftCamera: SupportedCamera;
  /** Camera used in the right view */
  rightCamera: SupportedCamera;
  /** Callback issued when the left camera should be animated to a new position */
  onLeftCameraMovedTo?(pos: Vector3): void;
  /** Callback issued when the right camera should be animated to a new position */
  onRightCameraMovedTo?(pos: Vector3): void;
};

/**
 * The minimap specialized for splitscreen mode. This component is an extension of the basic Minimap component. It provides
 * synchronization of the two camera positions when the minimap is clicked. It also manages the visualization of the two
 * cones representing the field of views of the left and right camera.
 *
 * @returns a component that instantiates the minimap in splitscreen mode
 */
export function SplitMinMap({
  trackingElement,
  canvasElement,
  actions,
  mainWalkElement,
  compareWalkElement,
  mainSceneFilter,
  compareSceneFilter,
  leftCamera,
  rightCamera,
  onPlaceholderClicked,
  onLeftCameraMovedTo,
  onRightCameraMovedTo,
}: SplitMiniMapProps): JSX.Element {
  const onMinimapClicked = useCallback(
    (pos: Vector3) => {
      if (mainSceneFilter !== SceneFilter.Pano) {
        // Keep the camera height as it is
        pos.y = leftCamera.position.y;
        onLeftCameraMovedTo?.(pos);
      }

      if (compareSceneFilter !== SceneFilter.Pano) {
        // Keep the camera height as it is
        pos.y = rightCamera.position.y;
        onRightCameraMovedTo?.(pos);
      }
    },
    [
      mainSceneFilter,
      compareSceneFilter,
      leftCamera.position.y,
      onLeftCameraMovedTo,
      rightCamera.position.y,
      onRightCameraMovedTo,
    ],
  );

  const onTrajectoryPointClicked = useCallback(
    (eyePosition: Vector3) => {
      if (mainSceneFilter !== SceneFilter.Pano) {
        onLeftCameraMovedTo?.(eyePosition);
      }
      if (compareSceneFilter !== SceneFilter.Pano) {
        onRightCameraMovedTo?.(eyePosition);
      }
    },
    [
      compareSceneFilter,
      mainSceneFilter,
      onLeftCameraMovedTo,
      onRightCameraMovedTo,
    ],
  );

  const isMainElementAligned = useAppSelector(
    selectIsPanoAligned(mainWalkElement),
  );
  const isCompareElementAligned = useAppSelector(
    selectIsPanoAligned(compareWalkElement),
  );

  const shouldShowViewCone =
    !isIElementImg2d(mainWalkElement) &&
    (!isIElementImg360(mainWalkElement) || isMainElementAligned);
  const shouldShowViewConeSecondCamera =
    !isIElementImg2d(compareWalkElement) &&
    (!isIElementImg360(compareWalkElement) || isCompareElementAligned);

  return (
    <MiniMap
      actions={actions}
      canvasElement={canvasElement}
      trackingElement={trackingElement}
      camera={leftCamera}
      secondScreenCamera={rightCamera}
      cameraPosition={leftCamera.position}
      showUserMarker
      shouldShowViewCone={shouldShowViewCone}
      shouldShowViewConeSecondCamera={shouldShowViewConeSecondCamera}
      onPlaceholderClicked={onPlaceholderClicked}
      onMinimapClicked={onMinimapClicked}
      onTrajectoryPointClicked={onTrajectoryPointClicked}
    />
  );
}
